import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';

interface CheckRow {
  id: string;
  number: string;
  check_number: string | null;
  payee_type: 'vendor' | 'employee' | 'other';
  payee_name: string;
  payment_date: string;
  currency: string;
  grand_total: string;
  net_amount: string;
  status: string;
}

const STATUS_CLASSES: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  void: 'bg-slate-100 text-slate-500',
};

export function Checks() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['bk-checks', activeOrgId],
    queryFn: () => api<{ checks: CheckRow[] }>('/bookkeeping/purchases/checks?limit=100'),
    enabled: !!activeOrgId,
  });
  const checks = data?.checks ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('purchases.checks.title')}</h1>
        <Link
          to="/app/bookkeeping/purchases/checks/new"
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('purchases.checks.new')}
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('purchases.checks.fields.number')}</th>
                <th className="px-3 py-2">{t('purchases.checks.fields.payee')}</th>
                <th className="px-3 py-2">{t('purchases.checks.fields.payment_date')}</th>
                <th className="px-3 py-2">{t('purchases.checks.fields.currency')}</th>
                <th className="px-3 py-2 text-right">{t('purchases.checks.fields.grand_total')}</th>
                <th className="px-3 py-2 text-right">{t('purchases.checks.fields.net_amount')}</th>
                <th className="px-3 py-2">{t('purchases.checks.fields.status')}</th>
              </tr>
            </thead>
            <tbody>
              {checks.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-slate-400">
                    {t('purchases.checks.empty')}
                  </td>
                </tr>
              )}
              {checks.map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/app/bookkeeping/purchases/checks/${c.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {c.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    {c.payee_name}
                    <span className="ml-1 text-xs text-slate-400">
                      ({t(`purchases.checks.payee_types.${c.payee_type}`)})
                    </span>
                  </td>
                  <td className="px-3 py-2">{c.payment_date}</td>
                  <td className="px-3 py-2">{c.currency}</td>
                  <td className="px-3 py-2 text-right font-mono">{c.grand_total}</td>
                  <td className="px-3 py-2 text-right font-mono">{c.net_amount}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-sm px-1.5 py-0.5 text-xs ${STATUS_CLASSES[c.status] ?? ''}`}
                    >
                      {t(`purchases.checks.statuses.${c.status}`, { defaultValue: c.status })}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
